import type { ReportListItem } from "../types/types.ts"
import { FOLDER_MAX_LENGTH } from "./reportSettings.ts"

const folderKey = (report: ReportListItem): string =>
  report.folder?.trim() ? report.folder : ""

export const distinctFolderNames = (reports: ReportListItem[]): string[] =>
  [
    ...new Set(
      reports
        .map((report) => report.folder)
        .filter((folder): folder is string => !!folder && folder.trim() !== "")
    )
  ].sort((a, b) => a.localeCompare(b))

/** Every folder the caller can file into or browse: the labels on the reports
 * they can list, plus their own folders that are still empty. */
export const knownFolderNames = (
  reports: ReportListItem[],
  ownFolders: string[]
): string[] =>
  [...new Set([...distinctFolderNames(reports), ...ownFolders])].sort((a, b) =>
    a.localeCompare(b)
  )

export const isUnfiled = (report: ReportListItem): boolean =>
  folderKey(report) === ""

export const canFileReport = (report: ReportListItem): boolean =>
  (report.access_level ?? "owner") !== "view"

export interface ReportFolderNavItem {
  name: string
  count: number
  /** Whether rename and delete would change anything for this caller: they
   * own the empty folder, or can edit a report filed in it. */
  manageable: boolean
}

/** The sidebar's folder rows. Counts are taken from the list the caller was
 * served, so they never include a report the caller cannot open, nor one
 * outside the branch scope that list was fetched under. */
export const buildFolderNav = (
  reports: ReportListItem[],
  ownFolders: string[]
): ReportFolderNavItem[] => {
  const own = new Set(ownFolders)
  return knownFolderNames(reports, ownFolders).map((name) => {
    const filed = reports.filter((report) => report.folder === name)
    return {
      name,
      count: filed.length,
      manageable: own.has(name) || filed.some(canFileReport)
    }
  })
}

export type FolderNameProblem = "required" | "tooLong" | "taken"

/** Checked before the request so the common mistakes are answered in the
 * field; the server re-checks every one of them. */
export const folderNameProblem = (
  name: string,
  existing: string[],
  current?: string
): FolderNameProblem | null => {
  const trimmed = name.trim()
  if (!trimmed) return "required"
  if (trimmed.length > FOLDER_MAX_LENGTH) return "tooLong"
  if (trimmed !== current && existing.includes(trimmed)) return "taken"
  return null
}

/** The message a refused folder request carries, from either envelope shape. */
export const folderRequestErrorMessage = (data: unknown): string | null => {
  const envelope = data as
    | {
        message?: unknown
        details?: {
          message?: unknown
          field_errors?: Record<string, unknown>
        }
        field_errors?: Record<string, unknown>
      }
    | undefined
  const fieldErrors =
    envelope?.details?.field_errors ?? envelope?.field_errors ?? {}
  for (const value of Object.values(fieldErrors)) {
    const first = Array.isArray(value) ? value[0] : value
    if (typeof first === "string" && first.trim()) return first
  }
  const message = envelope?.details?.message ?? envelope?.message
  return typeof message === "string" && message.trim() ? message : null
}
